import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { api } from '@/api/client';
import { ApiError, cuentaRestringidaDe, mensajeDe } from '@/api/errors';
import { useAuth } from '@/auth/AuthProvider';

import { AuthLayout, Campo } from './AuthLayout';

const schema = z
  .object({
    password: z.string().min(8, 'Mínimo 8 caracteres').max(128),
    confirmacion: z.string(),
  })
  .refine((v) => v.password === v.confirmacion, {
    path: ['confirmacion'],
    message: 'Las contraseñas no coinciden',
  });
type Form = z.infer<typeof schema>;

export function RestablecerPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { mostrarRestriccion } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  const onSubmit = async (form: Form) => {
    try {
      const { response, error } = await api.POST('/api/v1/auth/password/reset', {
        body: { token, password: form.password },
      });
      if (!response.ok) {
        const e = error as
          | { error?: { code?: string; message?: string; details?: Record<string, unknown> } }
          | undefined;
        throw new ApiError(
          response.status,
          e?.error?.code ?? 'HTTP_ERROR',
          e?.error?.message ?? 'No pudimos actualizar tu contraseña.',
          e?.error?.details ?? {},
        );
      }
      toast.success('Tu contraseña se actualizó. Inicia sesión con tu nueva contraseña.');
      void navigate('/login', { replace: true });
    } catch (err) {
      // Si la API llega a responder suspensión o bloqueo aquí, se avisa con el modal del login.
      const restringida = cuentaRestringidaDe(err);
      if (restringida) {
        mostrarRestriccion(restringida);
        void navigate('/login', { replace: true });
        return;
      }
      toast.error(mensajeDe(err));
    }
  };

  return (
    <AuthLayout titulo="Nueva contraseña">
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
        <Campo etiqueta="Nueva contraseña" error={errors.password?.message}>
          <input
            type="password"
            className="input-vidrio"
            autoComplete="new-password"
            {...register('password')}
          />
        </Campo>
        <Campo etiqueta="Confírmala" error={errors.confirmacion?.message}>
          <input
            type="password"
            className="input-vidrio"
            autoComplete="new-password"
            {...register('confirmacion')}
          />
        </Campo>
        <p className="text-xs text-texto-3">
          Por seguridad cerraremos tus sesiones abiertas en todos tus dispositivos.
        </p>
        <button type="submit" className="btn-cs btn-primario w-full py-3" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando…' : 'Guardar contraseña'}
        </button>
        <p className="text-center text-sm">
          <Link to="/recuperar-contrasenia">Solicitar otro enlace</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
